import React, { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import getConfig from 'next/config';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import AppLayout from 'components/layouts/AppLayout';
import { TNamespaceEnum } from 'types/types';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    StateInterface,
    ValidateValueType,
    ValueOnChangeRemoveType,
} from 'components/interfaces/GeneralInterface';
import {
    FieldTypeApiEnum,
    MembershipApiInterface,
    MembershipStatusEnum,
} from 'submodules/api_middleware';
import EditForm from 'components/editForm/EditForm';
import { OnActionType } from 'components/interfaces/EditFormInterface';
import {
    parseCzechBirthNumber,
    slashCzechBirthNumber,
    unslashCzechBirthNumber,
} from 'utils/index';
import { fetcherFull, MethodEnum } from 'utils/helpers/browser/newFetcher';
import { MessageType, pushMessage } from 'components/layouts/Toast';
import PaymentDetail from 'components/anonymous-payment/PaymentDetail';
import Button from 'components/forms/Button';
import { UserService, UserMe } from 'services/user.service';
import { getTokenCookie } from 'utils/helpers/browser/cookies';
import { TokenPurpose } from 'utils/helpers/cookies.shared';

import { getRoute } from 'constants/routes';
import styles from './AnonymousPaymentPage.module.scss';

/**
 * Page for personal (anonymous) payment. Has two fields (internal_id and birth_number). If combination is correct,
 * displays the payment instructions.
 */
const AnonymousPaymentPage = (): ReactElement => {
    const membersUrl = getRoute('/members');
    const router = useRouter();
    const { t } = useTranslation();
    const { publicRuntimeConfig } = getConfig();
    const is1Url = publicRuntimeConfig.NEXT_PUBLIC_IS1_URL;
    const bulkPaymentUrl = `${is1Url}/finance/hromadne-platby/?sport=fotbal`;
    const [displayNationalIdentifier, setDisplayNationalIdentifier] =
        useState(false);
    const [success, setSuccess] = useState(false);
    const [memberId, setMemberId] = useState(undefined);
    const [nationalId, setNationalId] = useState(undefined);
    const [user, setUser] = useState<UserMe>(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [userMembership, setUserMembership] =
        useState<MembershipApiInterface | null>(null);

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoadingUser(true);
                // Nejprve zkontrolujeme, zda je uživatel přihlášený pomocí tokenu
                const token = getTokenCookie(TokenPurpose.Access);

                if (token) {
                    // Pouze pokud máme token, zkusíme načíst uživatelská data
                    const userService = new UserService();
                    const userData = await userService.me();
                    if (userData) {
                        setUser(userData);

                        // Načteme informace o členství uživatele
                        try {
                            const membershipData =
                                await userService.myMembership();
                            setUserMembership(membershipData);
                        } catch (membershipError) {
                            console.log(
                                'Chyba při načítání členství uživatele'
                            );
                            setUserMembership(null);
                        }
                    }
                } else {
                    // Uživatel není přihlášený, nastavíme user na null
                    console.log(
                        'Uživatel není přihlášený, pokračujeme jako anonymní uživatel'
                    );
                    setUser(null);
                    setUserMembership(null);
                }
            } catch (error) {
                console.log(
                    'Chyba při načítání uživatele, pokračujeme jako anonymní uživatel'
                );
                setUser(null);
                setUserMembership(null);
            } finally {
                setLoadingUser(false);
            }
        };
        loadUser();
    }, []);

    const onPayForMyselfClick = () => {
        if (user?.member_internal_id) {
            setMemberId(user.member_internal_id);
            onCheckAnonymousPaymentMemberFormSubmit({
                member_id: user.member_internal_id,
                is_authenticated: '1',
            });
        }
    };

    const onFormSubmit: OnActionType = async (state) => {
        if (displayNationalIdentifier) {
            return onNationalIdentifierFormSubmit(state);
        } else {
            setMemberId(state.member_id);
            return onCheckAnonymousPaymentMemberFormSubmit(state);
        }
    };

    useEffect(() => {
        if (loadingUser) return;

        const queryMemberId = router.query.member_id as string | undefined;

        if (queryMemberId && !memberId) {
            setMemberId(queryMemberId);

            onCheckAnonymousPaymentMemberFormSubmit({
                member_id: queryMemberId,
                is_authenticated: user ? '1' : undefined,
            });
        }
    }, [router.query.member_id, loadingUser, user, memberId]);

    const onCheckAnonymousPaymentMemberFormSubmit: OnActionType = async (
        state
    ) => {
        const response = await fetcherFull<any>(
            '/api/anonymous-payment/check-member',
            MethodEnum.POST,
            undefined,
            state
        );
        if (response.error) {
            if (response.error == 'person_is_banned') {
                pushMessage(
                    getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'person_is_banned'
                    ),
                    MessageType.DANGER,
                    getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'person_is_banned_tittle'
                    ),
                    true
                );
            } else {
                pushMessage(
                    getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'invalid_anonymous_payment_text'
                    ),
                    MessageType.DANGER,
                    getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'invalid_member_id_title'
                    ),
                    true
                );
            }
            return response;
        }

        const isArchiveMember = response.data?.data?.is_archive_member;
        if (isArchiveMember) {
            await router.push(
                getRoute('/person-recovery', [], {
                    messageType: MessageType.DANGER,
                    message: getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'member_is_archive_text'
                    ),
                    messageTitle: getTranslation(
                        t,
                        TNamespaceEnum.anonymousPayment,
                        'member_is_archive_title'
                    ),
                })
            );
        }

        const isPaidMembership = response.data?.data?.is_paid_membership;
        if (isPaidMembership) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'membership_is_paid_title'
                ),
                true
            );
        } else {
            if (state.is_authenticated === '1') {
                setNationalId(null);
                setSuccess(true);
                setDisplayNationalIdentifier(false);
            } else {
                const isNationalIdRequired =
                    response.data?.data?.is_national_id_required;
                if (isNationalIdRequired) {
                    setDisplayNationalIdentifier(isNationalIdRequired);
                } else {
                    // move to the final step without setting the national identifier
                    // always set the national identifier before showing the payment data component
                    // otherwise, the BE API can be called with a wrong value of the national identifier
                    setNationalId(null);
                    setSuccess(true);
                }
            }
        }

        return response;
    };

    const onNationalIdentifierFormSubmit: OnActionType = async (state) => {
        const response = await fetcherFull<any>(
            '/api/anonymous-payment',
            MethodEnum.POST,
            undefined,
            state
        );

        if (response.error) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'membership_is_paid_title'
                ),
                true
            );
            return response;
        }

        const correct = response.data?.data?.correct;
        setNationalId(unslashCzechBirthNumber(state.national_identifier));
        setSuccess(correct);

        if (!correct) {
            pushMessage(
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_anonymous_payment_text'
                ),
                MessageType.DANGER,
                getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'invalid_combination_title'
                ),
                true
            );
        }

        return response;
    };

    // Form groups and fields for main form
    const mainFormFields: GroupEditFieldInterface[] = [
        {
            title: getTranslation(
                t,
                TNamespaceEnum.anonymousPayment,
                'anonymousPaymentTitle'
            ),
            fields: [
                {
                    name: 'member_id',
                    label: getTranslationKey(
                        TNamespaceEnum.anonymousPayment,
                        'form.member_id'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.FULL,
                    labelSize: LabelSize.L,
                    required: true,
                    disabled: displayNationalIdentifier || success, // the next or the final step
                    note: {
                        type: 'warning',
                        content: (
                            <>
                                Své{' '}
                                <strong>
                                    {getTranslation(
                                        t,
                                        TNamespaceEnum.anonymousPayment,
                                        'form.memberId'
                                    )}
                                </strong>{' '}
                                najdete v&nbsp;
                                <Link href={membersUrl}>
                                    <a
                                        className="inline-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {getTranslation(
                                            t,
                                            TNamespaceEnum.anonymousPayment,
                                            'form.membersDatabase'
                                        )}
                                    </a>
                                </Link>
                                .
                            </>
                        ),
                    },
                },
                {
                    name: 'national_identifier',
                    label: getTranslationKey(
                        TNamespaceEnum.anonymousPayment,
                        'form.national_identifier'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.FULL,
                    labelSize: LabelSize.L,
                    validate: (value: ValidateValueType) => {
                        const validationRes = parseCzechBirthNumber(
                            String(value)
                        );

                        // Invalid id
                        if (!validationRes.valid)
                            return t(validationRes.errorMessageKey);

                        return undefined;
                    },
                    displayIfSuccess: () => displayNationalIdentifier,
                    onChange: (
                        value: ValueOnChangeRemoveType,
                        state: StateInterface
                    ) => {
                        const strVal = String(value);
                        state.national_identifier = slashCzechBirthNumber(
                            true,
                            strVal
                        );
                        return state;
                    },
                    required: true,
                },
            ],
        },
    ];

    // Bulk membership info section (without buttons)
    const bulkMembershipFields: GroupEditFieldInterface[] = [
        {
            title: getTranslation(
                t,
                TNamespaceEnum.anonymousPayment,
                'bulkMembershipTitle'
            ),
            fields: [
                {
                    name: 'bulk_membership_info',
                    label: '',
                    type: FieldTypeApiEnum.REACT_DOM,
                    size: FilterSize.FULL,
                    value: (
                        <div>
                            <p
                                style={{
                                    marginBottom: '1.5rem',
                                    lineHeight: 1.5,
                                }}
                            >
                                {getTranslation(
                                    t,
                                    TNamespaceEnum.anonymousPayment,
                                    'bulkMembershipDescription'
                                )}
                            </p>
                            <a
                                href={bulkPaymentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    background: '#3d60d3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.75rem 1.5rem',
                                    fontWeight: '500',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        '#516fd4';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        '#3d60d3';
                                }}
                            >
                                📄{' '}
                                {getTranslation(
                                    t,
                                    TNamespaceEnum.anonymousPayment,
                                    'newDocumentButton'
                                )}
                            </a>
                        </div>
                    ),
                },
            ],
        },
    ];
    return (
        <AppLayout
            pageTitle={{
                primaryBoldText: getTranslation(
                    t,
                    TNamespaceEnum.anonymousPayment,
                    'pageTitle'
                ),
                isDark: true,
            }}
            hideUserMenu={false}
            showDisciplineMenu
            changeDisciplineWithoutReload={true}
            showLangMenu={true}
        >
            <div>
                {!success && (
                    <>
                        <div className={styles.formWrapper}>
                            <EditForm
                                fields={mainFormFields}
                                buttonTextKey={getTranslationKey(
                                    TNamespaceEnum.anonymousPayment,
                                    'form.submit'
                                )}
                                action={onFormSubmit}
                            />
                        </div>
                        {user &&
                            !loadingUser &&
                            userMembership &&
                            userMembership?.status !==
                                MembershipStatusEnum.active && (
                                <div className={styles.payForMyselfSection}>
                                    <p
                                        className={
                                            styles.payForMyselfDescription
                                        }
                                    >
                                        {getTranslation(
                                            t,
                                            TNamespaceEnum.anonymousPayment,
                                            'payForMyselfDescription'
                                        )}
                                    </p>
                                    <Button
                                        isTransparent
                                        onClick={onPayForMyselfClick}
                                    >
                                        {getTranslation(
                                            t,
                                            TNamespaceEnum.anonymousPayment,
                                            'form.payForMyself'
                                        )}
                                    </Button>
                                </div>
                            )}
                        <div className={styles.formWrapper}>
                            <EditForm fields={bulkMembershipFields} />
                        </div>
                    </>
                )}
                {success && (
                    <PaymentDetail
                        memberId={memberId}
                        nationalIdentifier={nationalId}
                        nationalIdentifierIsRequired={displayNationalIdentifier}
                    />
                )}
            </div>
        </AppLayout>
    );
};

export default AnonymousPaymentPage;
